import {type RunSignature} from '../../../types/run';
import {isAbortError} from '../../../util/requests';
import {
  type Thumbnails,
  type TileMedia,
  type TileMediaSpec,
} from '../../MediaCard';
import {
  fetchDirectURLByFilePath,
  invalidateDirectURLCacheForFilePath,
} from './batching/directURLs';
import {
  fetchFileBlob,
  getObjectURLForBlob,
  recordBackgroundBlobFetchFailure,
} from './blob';
import {isExpiredUrlError, tileMediaErrorKindFromBlobFailure} from './errors';
import IN_MEMORY_CACHE, {
  updateTileMediaStatus,
  updateTracking,
} from './inMemoryCache';
import {getKeyForRunSignature, getKeyForTileMediaSpec} from './keys';
import {type PathSpec} from './types';

export function shouldSkipBlobFetch(
  tileMedia: Pick<TileMedia, 'internalURL' | 'hlsSrc'>,
  tileMediaSpec: TileMediaSpec | undefined
) {
  // we skip fetching the blob entirely for images with an internal URL
  return tileMediaSpec?.mediaType === 'image' && tileMedia.internalURL != null;
}

export function shouldNotLogBlobFetchSuccess(
  tileMedia: Pick<TileMedia, 'internalURL' | 'hlsSrc'>,
  tileMediaSpec: TileMediaSpec | undefined,
  disableVideoStream: boolean
) {
  if (shouldSkipBlobFetch(tileMedia, tileMediaSpec)) {
    return true;
  }
  if (
    tileMediaSpec?.mediaType === 'video' &&
    tileMedia.hlsSrc != null &&
    !disableVideoStream
  ) {
    // we log success loading when the first chunk of video is loaded which is likely to happen prior to the blob fully loading,
    // so we shouldn't wait for the blob to fully load to emit the success event
    return true;
  }
  return false;
}

/**
 * Signed URLs expire (Gorilla signs them for an hour) but the direct-URL cache
 * has no TTL, so a tile fetched late in a long session can be handed a dead
 * URL. Drop the cached entry and ask Gorilla for a fresh one. Returns null
 * when the refetch produced no URL.
 */
async function refreshDirectURL(
  runSignature: RunSignature,
  filePath: string
): Promise<string | null> {
  invalidateDirectURLCacheForFilePath(runSignature, filePath);
  const [directURLs] = await fetchDirectURLByFilePath({
    runSignature,
    filePaths: [filePath],
  });
  return directURLs?.get(filePath) ?? null;
}

// Use all of the data we fetched to update the global cache.
// After this completes, the cache is ready for all Media Panels to use.
export async function updateWithNewData(
  tileMediaSpecsByRunSignatureKey: Map<string, TileMediaSpec[]>,
  existingFilePathByKey: Map<string, PathSpec>,
  directURLByRunFilePathOrArtifactURI: Map<string, string>,
  internalURLByRunFilePathOrArtifactURI: Map<string, string>,
  thumbnailsByRunFilePathOrArtifactURI: Map<string, Thumbnails | undefined>,
  hlsSrcByRunFilePathOrArtifactURI: Map<string, string | null | undefined>,
  disableVideoStream: boolean
): Promise<void> {
  const getTileMediaPromises: Array<Promise<void>> = [];
  for (const [, tileMediaSpecs] of tileMediaSpecsByRunSignatureKey) {
    for (const tileMediaSpec of tileMediaSpecs) {
      getTileMediaPromises.push(fetchWrapper(tileMediaSpec));
    }
  }
  await Promise.all(getTileMediaPromises);

  async function fetchWrapper(tileMediaSpec: TileMediaSpec) {
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
    try {
      await fetchBlobAndSetTileMedia(tileMediaSpec);
    } catch (error) {
      // Aborted (page unloading) is not a media error. Mark the tile as never
      // requested (tileMediaSpecIsNew treats !isRequested as new) so a page
      // restored from bfcache refetches instead of showing an error card.
      if (isAbortError(error)) {
        updateTileMediaStatus(tileMediaSpecKey, {
          isRequested: false,
          isLoading: false,
          hasError: false,
        });
        return;
      }
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorKind: 'media_fetch_failed',
        errorMessage:
          error instanceof Error ? error.message : 'Failed to fetch media',
      });
    }
  }

  async function fetchBlobAndSetTileMedia(
    tileMediaSpec: TileMediaSpec
  ): Promise<void> {
    const {runSignature} = tileMediaSpec;
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);

    const pathSpec = existingFilePathByKey.get(tileMediaSpecKey);
    if (pathSpec == null) {
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorKind: 'media_missing_data',
        errorMessage: 'No path specification found for media',
      });
      return;
    }

    const path =
      `filePath` in pathSpec ? pathSpec.filePath : pathSpec.artifactURI;
    const directURL = directURLByRunFilePathOrArtifactURI.get(
      `${getKeyForRunSignature(runSignature)}-${path}`
    );
    const internalURL = internalURLByRunFilePathOrArtifactURI.get(
      `${getKeyForRunSignature(runSignature)}-${path}`
    );
    const thumbnails = thumbnailsByRunFilePathOrArtifactURI.get(
      `${getKeyForRunSignature(runSignature)}-${path}`
    );
    const hlsSrc = hlsSrcByRunFilePathOrArtifactURI.get(
      `${getKeyForRunSignature(runSignature)}-${path}`
    );
    if (directURL == null) {
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorKind: 'media_missing_url',
        errorMessage: 'No direct URL found for media',
      });
      return;
    }

    const details = {
      step: tileMediaSpec.mediaStep,
      index: tileMediaSpec.mediaIndex,
      historyRow: pathSpec.historyRow,
      filePath: path,
      directURL,
      internalURL,
      mediaSliderKey: tileMediaSpec.mediaSliderKey,
      mediaSliderKeyValue: tileMediaSpec.mediaSliderKeyValue,
      thumbnails,
      hlsSrc,
    };

    const nullBlob = new Blob();

    IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.set(tileMediaSpecKey, {
      ...details,
      mediaKey: tileMediaSpec.mediaKey,
      isNullBlob: true,
      blob: nullBlob,
      objectURL: getObjectURLForBlob(nullBlob),
    });

    const logBlobFetchSuccess = !shouldNotLogBlobFetchSuccess(
      {internalURL, hlsSrc},
      tileMediaSpec,
      disableVideoStream
    );

    if (!logBlobFetchSuccess) {
      // Capture a single timestamp so blobFetch and cacheUpdate are both 0ms
      // rather than blobFetch going negative (blobFetchComplete stays 0) and
      // cacheUpdate absorbing the entire span.
      const now = performance.now();
      updateTracking(tileMediaSpecKey, 'blobFetchCompletePerformanceMs', now);
      updateTracking(tileMediaSpecKey, 'dataCachedPerformanceMs', now);
      updateTileMediaStatus(tileMediaSpecKey, {
        isLoaded: true,
        hasError: false,
      });
    }
    if (shouldSkipBlobFetch({internalURL, hlsSrc}, tileMediaSpec)) {
      return;
    }

    // When the tile is already loaded (streamed video), the blob only backs the
    // download button, so a failure is not an error the user sees.
    const reportBackgroundBlobFailure = (errorMessage: string) =>
      recordBackgroundBlobFetchFailure(tileMediaSpec.mediaType, errorMessage);

    const fetchOptions = {logFailure: logBlobFetchSuccess};
    // Artifact URIs are resolved elsewhere, so only run files are refreshed.
    const fetchBlobRefreshingExpiredURL = async (): Promise<Blob | null> => {
      try {
        return await fetchFileBlob(directURL, fetchOptions);
      } catch (e) {
        if (!isExpiredUrlError(e) || !('filePath' in pathSpec)) {
          throw e;
        }
        const freshURL = await refreshDirectURL(
          runSignature,
          pathSpec.filePath
        );
        if (freshURL == null || freshURL === directURL) {
          throw e;
        }
        details.directURL = freshURL;
        return await fetchFileBlob(freshURL, fetchOptions);
      }
    };

    try {
      const blob = await fetchBlobRefreshingExpiredURL();
      if (blob == null) {
        if (!logBlobFetchSuccess) {
          reportBackgroundBlobFailure('Failed to fetch blob data');
          return;
        }
        updateTileMediaStatus(tileMediaSpecKey, {
          hasError: true,
          errorKind: 'media_fetch_failed',
          errorMessage: 'Failed to fetch blob data',
        });
        return;
      }

      if (logBlobFetchSuccess) {
        // Track blob fetch completion (network download complete)
        updateTracking(tileMediaSpecKey, 'blobFetchCompletePerformanceMs');
      }

      IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.set(tileMediaSpecKey, {
        ...details,
        mediaKey: tileMediaSpec.mediaKey,
        isNullBlob: false,
        blob,
        objectURL: getObjectURLForBlob(blob),
      });

      if (logBlobFetchSuccess) {
        // Track cache update completion (memory operations complete)
        updateTracking(tileMediaSpecKey, 'dataCachedPerformanceMs');

        updateTileMediaStatus(tileMediaSpecKey, {
          isLoaded: true,
          hasError: false,
        });
      }
    } catch (e) {
      // Same as above: an abort resets the tile so it can be refetched.
      if (isAbortError(e)) {
        updateTileMediaStatus(tileMediaSpecKey, {
          isRequested: false,
          isLoading: false,
          hasError: false,
        });
        return;
      }
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to process media data';
      if (!logBlobFetchSuccess) {
        reportBackgroundBlobFailure(errorMessage);
        return;
      }
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorKind: isExpiredUrlError(e)
          ? tileMediaErrorKindFromBlobFailure(e)
          : 'media_fetch_failed',
        errorMessage,
      });
    }
  }
}
